import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import XIcon from './icons/XIcon';

const Footer = () => {
  const location = useLocation();
  const hideNewsletter = location.pathname === '/buy';
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [socialLinks, setSocialLinks] = useState({
    linkedin: '',
    twitter: '',
    youtube: ''
  });

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social-links`);
      const data = await response.json();
      setSocialLinks(data);
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ first_name: firstName, email, source: 'footer' }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        setEmail('');
        setFirstName('');
        setTimeout(() => setSubmitted(false), 3000);
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect. Please try again later.');
      console.error('Newsletter signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      style={{
        backgroundColor: '#111111',
        color: '#F4F1E8',
        padding: '80px 40px 40px'
      }}
    >
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}
      >
        {/* Newsletter Section */}
        {!hideNewsletter && (
        <div
          id="email-signup"
          style={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            paddingBottom: '60px',
            marginBottom: '60px'
          }}
        >
          <div
            style={{
              maxWidth: '600px',
              margin: '0 auto',
              textAlign: 'center'
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '2rem',
                marginBottom: '16px',
                color: '#F4F1E8'
              }}
            >
              Stay Updated
            </h3>
            <p
              style={{
                fontSize: '1.1rem',
                marginBottom: '32px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}
            >
              Be the first to hear about events, speaking dates and news worth your time.
            </p>
            {submitted ? (
              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'rgba(201, 162, 75, 0.18)',
                  border: '1px solid #C9A24B',
                  borderRadius: '4px',
                  color: '#F4F1E8'
                }}
              >
                Thank you for subscribing!
              </div>
            ) : (
              <>
                {error && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: 'rgba(255, 0, 0, 0.15)',
                      border: '1px solid rgba(255, 0, 0, 0.3)',
                      borderRadius: '4px',
                      color: '#F4F1E8',
                      fontSize: '0.9rem',
                      marginBottom: '12px'
                    }}
                  >
                    {error}
                  </div>
                )}
                <form
                  onSubmit={handleNewsletterSubmit}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    maxWidth: '500px',
                    margin: '0 auto',
                    flexWrap: 'wrap'
                  }}
                >
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    required
                    disabled={loading}
                    data-testid="footer-subscribe-firstname"
                  style={{
                    flex: '1',
                    minWidth: '250px',
                    padding: '14px 20px',
                    fontSize: '1rem',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#F4F1E8'
                  }}
                />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    disabled={loading}
                    data-testid="footer-subscribe-email"
                  style={{
                    flex: '1',
                    minWidth: '250px',
                    padding: '14px 20px',
                    fontSize: '1rem',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#F4F1E8'
                  }}
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-subscribe-button"
                  style={{
                    padding: '14px 32px',
                    backgroundColor: loading ? '#999999' : '#C9A24B',
                    color: '#111111',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.target.style.backgroundColor = '#E3C46A';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.target.style.backgroundColor = '#C9A24B';
                  }}
                >
                  {loading ? 'Submitting...' : 'Keep me posted'}
                </button>
              </form>
              </>
            )}
          </div>
        </div>
        )}

        {/* Footer Content */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            marginBottom: '60px'
          }}
        >
          {/* About Section */}
          <div>
            <h4
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.5rem',
                marginBottom: '16px',
                color: '#F4F1E8'
              }}
            >
              The Goldilocks Zone
            </h4>
            <p
              style={{
                fontSize: '0.95rem',
                lineHeight: '1.7',
                color: 'rgba(255, 255, 255, 0.7)'
              }}
            >
              Creating hospitable organisations in inhospitable environments.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '16px',
                color: '#F4F1E8',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Quick Links
            </h4>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}
            >
              {[
                { name: 'About the Book', path: '/about-the-book' },
                { name: 'About the Authors', path: '/about-the-authors' },
                { name: 'Media & Events', path: '/media-events' },
                { name: 'Buy the Book', path: '/buy' }
              ].map((link) => (
                <li key={link.path} style={{ marginBottom: '8px' }}>
                  <Link
                    to={link.path}
                    style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#C9A24B';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                    }}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '16px',
                color: '#F4F1E8',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Connect
            </h4>
            <div style={{ display: 'flex', gap: '16px' }}>
              <a
                href={socialLinks.linkedin || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
                style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#C9A24B'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'; }}
              >
                <Linkedin size={24} />
              </a>
              <a
                href={socialLinks.twitter || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                data-testid="footer-x"
                style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#C9A24B'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'; }}
              >
                <XIcon size={22} />
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div
          style={{
            textAlign: 'center',
            paddingTop: '40px',
            borderTop: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.9rem'
          }}
        >
          <p>© {new Date().getFullYear()} The Goldilocks Zone. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
;
